import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { fetchEnrichedData } from "@/services/enrichedDataService";
import { fetchWeatherData } from "@/services/weatherService";
import {
  fetchRegionAirQuality,
  ProcessedAirQualityData,
} from "@/services/airQualityService";
import {
  getRegionCarbonData,
  ProcessedCarbonData,
  generateSectorBreakdown,
} from "@/services/carbonService";
import {
  Calendar,
  Thermometer,
  Droplets,
  Wind,
  Map,
  FileText,
  Leaf,
  AlertTriangle,
  Rainbow,
  Zap,
  Eye,
  TrendingUp,
  TrendingDown,
  Activity,
  CloudRain,
  Sun,
  Snowflake,
  Gauge,
  MapPin,
  BarChart3,
  Factory,
  TreePine,
  Car,
  Lightbulb,
  Award,
  Target,
  Flame,
  Mountain,
  ExternalLink,
  Clock,
  Waves,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fetchRegionById } from "@/services/environmentalDataService";
import { getSpeciesImage } from "@/lib/inaturalist";
import { cn } from "@/lib/utils";
import { useTheme } from "@/context/ThemeContext";
import DataSourceInfo from "@/components/DataSourceInfo";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import AirQualityDisplay from "@/components/ui/AirQualityDisplay";
import {
  fetchExtremeWeatherSummary,
  ExtremeWeatherSummary,
  NaturalEvent,
} from "@/services/extremeWeatherService";

interface EnrichedDataTabProps {
  regionId: string;
}

const FALLBACK_IMAGE = "/fallback_image.jpg";

// ─── Helpers for the Extreme Weather tab ─────────────────────────────────────

const ALERT_COLORS: Record<string, string> = {
  Red: "bg-red-500/15 text-red-600 border-red-500/30",
  Orange: "bg-orange-500/15 text-orange-600 border-orange-500/30",
  Yellow: "bg-yellow-500/15 text-yellow-700 border-yellow-500/30",
  Green: "bg-green-500/15 text-green-600 border-green-500/30",
};

const SOURCE_COLORS: Record<string, string> = {
  EONET: "bg-blue-500/15 text-blue-600",
  USGS: "bg-orange-500/15 text-orange-600",
  GDACS: "bg-red-500/15 text-red-600",
};

const CATEGORY_META: Record<
  string,
  { icon: React.ComponentType<any>; color: string; barColor: string }
> = {
  Wildfire: { icon: Flame, color: "text-orange-500", barColor: "#f97316" },
  Flood: { icon: Waves, color: "text-blue-500", barColor: "#3b82f6" },
  Storm: { icon: CloudRain, color: "text-purple-500", barColor: "#8b5cf6" },
  Earthquake: { icon: Activity, color: "text-red-500", barColor: "#ef4444" },
  Volcano: { icon: Mountain, color: "text-rose-600", barColor: "#dc2626" },
  Drought: { icon: Sun, color: "text-yellow-500", barColor: "#eab308" },
  Landslide: {
    icon: AlertTriangle,
    color: "text-stone-500",
    barColor: "#78716c",
  },
  Other: { icon: Zap, color: "text-gray-500", barColor: "#6b7280" },
};

const CHART_CATEGORIES = [
  "Wildfire",
  "Flood",
  "Storm",
  "Earthquake",
  "Volcano",
  "Drought",
  "Landslide",
  "Other",
] as const;

const EnrichedDataTab: React.FC<EnrichedDataTabProps> = ({ regionId }) => {
  // Only declare theme once at the top of the component
  const themeContext = useTheme();
  const theme = themeContext?.theme;
  // Tooltip style based on theme
  const tooltipStyle = React.useMemo(
    () =>
      theme === "dark"
        ? {
            backgroundColor: "#18181b", // zinc-900
            color: "#f4f4f5", // zinc-100 - text color
            border: "1px solid #27272a", // zinc-800
            borderRadius: "12px",
            fontFamily: "Inter, sans-serif",
            boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
            // Ensure text elements inherit the color
            WebkitFontSmoothing: "antialiased",
          }
        : {
            backgroundColor: "#fff",
            color: "#18181b", // zinc-900 - text color
            border: "1px solid #e5e7eb", // gray-200
            borderRadius: "12px",
            fontFamily: "Inter, sans-serif",
            boxShadow: "0 4px 6px rgba(0,0,0,0.08)",
          },
    [theme],
  );
  const [activeTab, setActiveTab] = useState("weather");
  const [data, setData] = useState<any>(null);
  const [weatherData, setWeatherData] = useState<any>(null);
  const [airQualityData, setAirQualityData] =
    useState<ProcessedAirQualityData | null>(null);
  const [airQualityLoading, setAirQualityLoading] = useState(false);
  const [airQualityError, setAirQualityError] = useState<string | null>(null);
  const [carbonData, setCarbonData] = useState<ProcessedCarbonData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [carbonLoading, setCarbonLoading] = useState(false);
  const [regionInfo, setRegionInfo] = useState<{
    name: string;
    country: string;
  } | null>(null);
  const [regionCoords, setRegionCoords] = useState<[number, number] | null>(
    null,
  );
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);
  const [extremeWeatherSummary, setExtremeWeatherSummary] =
    useState<ExtremeWeatherSummary | null>(null);
  const [extremeWeatherLoading, setExtremeWeatherLoading] = useState(false);
  const [extremeWeatherError, setExtremeWeatherError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setCarbonLoading(true);
        setError(null);

        // Load weather data separately
        console.log("Loading weather data for region:", regionId);
        const weather = await fetchWeatherData(regionId);
        console.log("Weather data loaded:", weather);
        setWeatherData(weather);

        // Load air quality data separately
        setAirQualityLoading(true);
        setAirQualityError(null);
        try {
          console.log("🌬️ Loading air quality data for region:", regionId);
          const airQuality = await fetchRegionAirQuality(regionId);
          if (airQuality) {
            console.log("🌬️ Air quality data loaded successfully:", airQuality);
            setAirQualityData(airQuality);
            setAirQualityError(null);
          } else {
            console.log(
              "🌬️ No air quality data available for region:",
              regionId,
            );
            setAirQualityData(null);
            setAirQualityError(
              "No air quality monitoring stations found for this region",
            );
          }
        } catch (airQualityErr) {
          console.error("🌬️ Error loading air quality data:", airQualityErr);
          setAirQualityData(null);
          setAirQualityError(
            airQualityErr instanceof Error
              ? airQualityErr.message
              : "Failed to load air quality data",
          );
        } finally {
          setAirQualityLoading(false);
        }

        // Load carbon analytics data
        console.log("🌱 Loading carbon data for region:", regionId);
        try {
          const carbonAnalytics = await getRegionCarbonData(regionId);
          if (carbonAnalytics) {
            console.log("🌱 Carbon data loaded successfully:", carbonAnalytics);
            setCarbonData(carbonAnalytics);
          } else {
            console.log("🌱 No carbon data available for region:", regionId);
            setCarbonData(null);
          }
        } catch (carbonError) {
          console.error("🌱 Error loading carbon data:", carbonError);
          setCarbonData(null);
        }
        setCarbonLoading(false);

        // Load other enriched data
        const enrichedData = await fetchEnrichedData(regionId);
        setData(enrichedData);
      } catch (error) {
        console.error("Error loading enriched data:", error);
        setError("Failed to load enriched data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (regionId) {
      loadData();
    }
  }, [regionId]);

  useEffect(() => {
    const loadRegionInfo = async () => {
      if (regionId) {
        console.log("🌍 [REGION DEBUG] Fetching region info for ID:", regionId);
        const region = await fetchRegionById(regionId);
        console.log("🌍 [REGION DEBUG] Region data received:", region);
        if (region) {
          console.log("🌍 [REGION DEBUG] Setting region info:", {
            name: region.name,
            country: region.country,
          });
          setRegionInfo({ name: region.name, country: region.country });
          if (region.coordinates) {
            // coordinates are stored as [lng, lat] in PostGIS convention
            setRegionCoords(region.coordinates as [number, number]);
          }
        } else {
          console.log(
            "🌍 [REGION DEBUG] ❌ No region data found for ID:",
            regionId,
          );
        }
      }
    };
    loadRegionInfo();
  }, [regionId]);

  useEffect(() => {
    const fetchBgImage = async () => {
      if (regionInfo?.name) {
        const img = await getSpeciesImage(regionInfo.name);
        if (img?.url) setBgImageUrl(img.url);
        else setBgImageUrl(FALLBACK_IMAGE);
      } else {
        setBgImageUrl(FALLBACK_IMAGE);
      }
    };
    fetchBgImage();
  }, [regionInfo]);

  // Load extreme weather data once we know the region coordinates.
  // Sources: NASA EONET + USGS Earthquake Catalog + GDACS (fired in parallel).
  useEffect(() => {
    const loadExtremeWeather = async () => {
      if (!regionCoords) return;
      setExtremeWeatherLoading(true);
      setExtremeWeatherError(null);
      try {
        // regionCoords is stored as [lng, lat] (PostGIS / GeoJSON convention)
        const [lng, lat] = regionCoords;
        const summary = await fetchExtremeWeatherSummary(lat, lng);
        setExtremeWeatherSummary(summary);
      } catch (err) {
        console.error("[ExtremeWeather] Failed to load summary:", err);
        setExtremeWeatherError(
          err instanceof Error
            ? err.message
            : "Failed to load extreme weather data",
        );
      } finally {
        setExtremeWeatherLoading(false);
      }
    };
    loadExtremeWeather();
  }, [regionCoords]);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-in fade-in duration-300">
        {/* Loading header */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          <span className="font-medium">Enriched data loading…</span>
        </div>

        {/* Tab bar skeleton */}
        <div className="grid grid-cols-4 gap-1 rounded-lg bg-muted/50 p-1">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-9 rounded-md" />
          ))}
        </div>

        {/* Hero card skeleton */}
        <div className="rounded-2xl border border-border bg-muted/30 p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-52" />
            </div>
            <Skeleton className="h-16 w-16 rounded-full shrink-0" />
          </div>
        </div>

        {/* Four metric cards skeleton */}
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-card p-4 space-y-3"
            >
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chart skeleton */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-20" />
          </div>
          {/* Simulated bar chart */}
          <div className="flex items-end gap-2 h-36 pt-2">
            {[55, 70, 45, 80, 60, 90, 50, 75, 65, 85, 40, 72].map((pct, i) => (
              <div key={i} className="flex-1 flex flex-col justify-end">
                <Skeleton
                  className="w-full rounded-sm"
                  style={{ height: `${pct}%` }}
                />
              </div>
            ))}
          </div>
          {/* X-axis labels */}
          <div className="flex gap-2">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className="flex-1 h-2.5 rounded-sm" />
            ))}
          </div>
        </div>

        {/* Status strip */}
        <p className="text-xs text-center text-muted-foreground pb-2">
          Fetching climate, weather &amp; air quality data…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">Error Loading Data</h3>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!data && !weatherData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No Data Available</h3>
        <p className="text-sm text-muted-foreground">
          Enriched data is not available for this region.
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="weather">
              <Thermometer className="h-4 w-4 mr-2" />
              Weather
            </TabsTrigger>
            <TabsTrigger value="climate">
              <Calendar className="h-4 w-4 mr-2" />
              Climate
            </TabsTrigger>
            <TabsTrigger value="carbon">
              <BarChart3 className="h-4 w-4 mr-2" />
              Carbon Analytics
            </TabsTrigger>
            <TabsTrigger value="extreme">
              <Zap className="h-4 w-4 mr-2" />
              Extreme Weather
            </TabsTrigger>
          </TabsList>

          <TabsContent value="weather" className="space-y-6">
            {weatherData ? (
              <>
                {/* Weather Hero Card */}
                <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-500/10 via-blue-600/5 to-cyan-500/10">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent" />
                  <div className="relative p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      {/* Location Header */}
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/20">
                          <MapPin className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold">
                            {regionInfo?.name || "Region"}
                          </h2>
                          <p className="text-muted-foreground text-left">
                            {regionInfo?.country || "Country"}
                          </p>
                        </div>
                      </div>

                      {/* Current Weather Display */}
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          {weatherData?.icon ? (
                            <img
                              src={`https://openweathermap.org/img/wn/${weatherData.icon}@4x.png`}
                              alt={weatherData?.description || "Weather icon"}
                              className="h-20 w-20 mx-auto"
                            />
                          ) : (
                            <Thermometer className="h-20 w-20 text-blue-500 mx-auto" />
                          )}
                          <p className="text-sm text-muted-foreground mt-2 capitalize">
                            {weatherData?.description || "No description"}
                          </p>
                        </div>
                        <div className="text-center">
                          <div className="text-5xl font-light">
                            {weatherData?.temperature || "--"}°
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            Feels like {weatherData?.feels_like || "--"}°
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weather Metrics Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-blue-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
                        <Droplets className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">
                          {weatherData?.humidity || "--"}%
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Humidity
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-green-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                        <Wind className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">
                          {weatherData?.wind_speed || "--"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Wind Speed
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-purple-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/10">
                        <Gauge className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">
                          {weatherData?.pressure || "--"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Pressure
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-orange-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/10">
                        <Eye className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">
                          {weatherData?.visibility || "--"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Visibility
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Air Quality Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Air Quality</h3>
                    <span className="text-sm text-muted-foreground">
                      Real-time monitoring
                    </span>
                  </div>
                  <AirQualityDisplay
                    data={airQualityData}
                    loading={airQualityLoading}
                    error={airQualityError}
                  />
                </div>

                {/* Data Source Information */}
                <DataSourceInfo
                  dataType="weather"
                  updatedAt={weatherData?.updatedAt || new Date().toISOString()}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No Weather Data Available
                </h3>
                <p className="text-sm text-muted-foreground">
                  Unable to fetch weather data for this region.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="climate" className="space-y-6">
            {data?.climate ? (
              <>
                {/* Hero Climate Metrics Card */}
                <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-background via-muted/20 to-muted/40 p-6">
                  <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-sm text-muted-foreground">
                          Climate Analysis
                        </span>
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-600">
                        {data?.climate?.climateZone || "Unknown Zone"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Thermometer className="h-4 w-4" />
                          Average Temperature
                        </div>
                        <div className="text-4xl text-left font-bold text-primary">
                          {data?.climate?.averageTemperature || "--"}°C
                        </div>
                        <div className="text-left space-y-1 text-sm">
                          <p className="text-muted-foreground">
                            Max:{" "}
                            <span className="text-orange-500">
                              {data?.climate?.indicators?.maxTemperature ||
                                "--"}
                              °C
                            </span>
                          </p>
                          <p className="text-muted-foreground">
                            Min:{" "}
                            <span className="text-blue-500">
                              {data?.climate?.indicators?.minTemperature ||
                                "--"}
                              °C
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Droplets className="h-4 w-4" />
                          Annual Precipitation
                        </div>
                        <div className="text-4xl text-left font-bold text-primary">
                          {data?.climate?.averagePrecipitation || "--"} mm
                        </div>
                        <p className="text-sm text-left text-muted-foreground">
                          Wet Days:{" "}
                          <span className="text-emerald-500 text-left">
                            {data?.climate?.indicators?.wetDays || "--"}{" "}
                            days/year
                          </span>
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Wind className="h-4 w-4" />
                          Climate Zone
                        </div>
                        <div className="text-2xl text-left font-bold text-primary">
                          {data?.climate?.climateZone || "Unknown"}
                        </div>
                        <p className="text-sm text-left text-muted-foreground">
                          Köppen classification
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Climate Indicators */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Climate Extremes</h3>
                    <span className="text-sm text-muted-foreground">
                      5-year analysis
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-orange-500/30">
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/10">
                          <Sun className="h-5 w-5 text-orange-500" />
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-orange-500">
                            {data.climate.indicators?.hotDays || "--"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Hot Days ({">"}35°C)
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-blue-500/30">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
                          <Snowflake className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-blue-500">
                            {data.climate.indicators?.frostDays || "--"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Frost Days ({"<"}0°C)
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-emerald-500/30">
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                          <Droplets className="h-5 w-5 text-emerald-500" />
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-emerald-500">
                            {data.climate.indicators?.wetDays || "--"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Wet Days ({">"}1mm)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Historical Trends Chart */}
                {data.climate?.historicalTrends?.years?.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold">
                        Historical Climate Trends
                      </h3>
                      <div className="text-sm text-muted-foreground">
                        {data.climate.historicalTrends.years.length} years of
                        data
                      </div>
                    </div>
                    <div className="rounded-xl border border-border bg-card p-6">
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart
                          data={data.climate.historicalTrends.years.map(
                            (year, index) => ({
                              year,
                              temperature:
                                data.climate.historicalTrends.temperature[
                                  index
                                ],
                              precipitation:
                                data.climate.historicalTrends.precipitation[
                                  index
                                ],
                            }),
                          )}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <defs>
                            <linearGradient
                              id="climate-temp-gradient"
                              x1="0"
                              y1="0"
                              x2="1"
                              y2="0"
                            >
                              <stop offset="0%" stopColor="#ef4444" />
                              <stop offset="100%" stopColor="#dc2626" />
                            </linearGradient>
                            <linearGradient
                              id="climate-precip-gradient"
                              x1="0"
                              y1="0"
                              x2="1"
                              y2="0"
                            >
                              <stop offset="0%" stopColor="#3b82f6" />
                              <stop offset="100%" stopColor="#1d4ed8" />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="hsl(var(--border))"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="year"
                            stroke="hsl(var(--muted-foreground))"
                            tick={{
                              fontWeight: 500,
                              fontSize: 12,
                              fontFamily: "Inter, sans-serif",
                            }}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={10}
                          />
                          <YAxis
                            yAxisId="left"
                            stroke="hsl(var(--muted-foreground))"
                            tick={{
                              fontWeight: 500,
                              fontSize: 12,
                              fontFamily: "Inter, sans-serif",
                            }}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={10}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke="hsl(var(--muted-foreground))"
                            tick={{
                              fontWeight: 500,
                              fontSize: 12,
                              fontFamily: "'Manrope', sans-serif",
                            }}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={10}
                          />
                          <RechartsTooltip
                            contentStyle={tooltipStyle}
                            formatter={(value, name) => {
                              const color =
                                name === "Temperature" ? "#ef4444" : "#3b82f6";
                              const unit = name === "Temperature" ? "°C" : "mm";
                              return [
                                <span style={{ color }}>
                                  {value}
                                  {unit}
                                </span>,
                                name,
                              ];
                            }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="temperature"
                            stroke="url(#climate-temp-gradient)"
                            strokeWidth={3}
                            name="Temperature"
                            connectNulls={false}
                            dot={{ r: 4, fill: "#ef4444" }}
                            activeDot={{
                              r: 6,
                              stroke: "#ef4444",
                              strokeWidth: 2,
                              fill: "#ef4444",
                            }}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="precipitation"
                            stroke="url(#climate-precip-gradient)"
                            strokeWidth={3}
                            name="Precipitation"
                            connectNulls={false}
                            dot={{ r: 4, fill: "#3b82f6" }}
                            activeDot={{
                              r: 6,
                              stroke: "#3b82f6",
                              strokeWidth: 2,
                              fill: "#3b82f6",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Data Source Information */}
                <DataSourceInfo
                  dataType="climate"
                  updatedAt={data?.updatedAt || new Date().toISOString()}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No Climate Data Available
                </h3>
                <p className="text-sm text-muted-foreground">
                  Unable to fetch climate data for this region.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="carbon" className="space-y-6">
            {carbonLoading ? (
              <div className="space-y-6">
                <div className="h-48 bg-muted animate-pulse rounded-2xl" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-24 bg-muted animate-pulse rounded-xl"
                    />
                  ))}
                </div>
                <div className="h-80 bg-muted animate-pulse rounded-xl" />
              </div>
            ) : carbonData ? (
              <>
                {/* Carbon Analytics Hero Card */}
                <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-emerald-500/10 via-green-600/5 to-teal-500/10">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent" />
                  <div className="relative p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      {/* Carbon Analytics Header */}
                      <div className="flex items-center gap-3">
                        <div className="text-left">
                          <h2 className="text-2xl font-bold">
                            Carbon & Emissions Analytics
                          </h2>
                          <p className="text-muted-foreground">
                            Regional carbon footprint & sustainability insights
                            for {regionInfo?.name || "this region"}
                          </p>
                        </div>
                      </div>

                      {/* ESG Rating & Status */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="text-right">
                          <div className="text-3xl font-bold text-emerald-600">
                            {carbonData.totalEmissions} Mt
                          </div>
                          <div className="text-sm text-muted-foreground">
                            CO₂ Emissions ({carbonData.latestYear})
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                className={`cursor-help ${
                                  carbonData.esgRating.startsWith("A")
                                    ? "bg-green-500/10 text-green-600"
                                    : carbonData.esgRating.startsWith("B")
                                      ? "bg-blue-500/10 text-blue-600"
                                      : "bg-orange-500/10 text-orange-600"
                                }`}
                              >
                                ESG Rating: {carbonData.esgRating}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              <p className="font-semibold mb-1">
                                Internal ESG estimate — not an official rating
                              </p>
                              <p>
                                A+ / A: emissions ≥15–30 % below historic peak
                                and still declining
                              </p>
                              <p>
                                B+ / B: emissions below peak or holding stable
                              </p>
                              <p>
                                C+ / C: emissions near or above historic peak
                              </p>
                              <p className="mt-1 text-muted-foreground">
                                Based on regional Supabase historical data.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge
                            className={`${
                              carbonData.trend === "decreasing"
                                ? "bg-green-500/10 text-green-600"
                                : carbonData.trend === "stable"
                                  ? "bg-blue-500/10 text-blue-600"
                                  : "bg-red-500/10 text-red-600"
                            }`}
                          >
                            {carbonData.trend === "decreasing"
                              ? "📉 Improving"
                              : carbonData.trend === "stable"
                                ? "📊 Stable"
                                : "📈 Rising"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Carbon Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 hover:shadow-lg transition-all duration-200 hover:border-emerald-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
                        <Activity className="h-6 w-6 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-600">
                          {carbonData.totalEmissions}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Current Emissions (Mt CO₂)
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 hover:shadow-lg transition-all duration-200 hover:border-blue-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10">
                        {carbonData.growthRate >= 0 ? (
                          <TrendingUp className="h-6 w-6 text-red-500" />
                        ) : (
                          <TrendingDown className="h-6 w-6 text-green-500" />
                        )}
                      </div>
                      <div>
                        <div
                          className={`text-2xl font-bold ${carbonData.growthRate >= 0 ? "text-red-500" : "text-green-500"}`}
                        >
                          {carbonData.growthRate > 0 ? "+" : ""}
                          {carbonData.growthRate}%
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Annual Growth Rate
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 hover:shadow-lg transition-all duration-200 hover:border-purple-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/10">
                        <Target className="h-6 w-6 text-purple-500" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-600">
                          {carbonData.reductionTarget}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          IPCC 1.5°C Target 2030 (Mt CO₂)
                        </div>
                        <div className="text-xs text-muted-foreground/70">
                          43% reduction · indicative estimate
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 hover:shadow-lg transition-all duration-200 hover:border-amber-500/30">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
                        <Award className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-amber-600">
                          {carbonData.peakEmissions}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Peak Year ({carbonData.peakYear})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Emissions by Sector */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">Emissions by Sector</h3>
                    <div className="text-sm text-muted-foreground">
                      Global average estimates for {carbonData.latestYear}
                    </div>
                  </div>
                  {/* Disclaimer above the data */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Note:</strong> Sector breakdowns are estimated
                        using IEA global averages (Energy 73%, Industry 16%,
                        Transport 7%, Agriculture 4%). Actual country profiles
                        may vary significantly.
                      </p>
                    </div>
                  </div>
                  {/* Compact horizontal bar list */}
                  <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                    {generateSectorBreakdown(carbonData.totalEmissions).map(
                      (sector) => {
                        const IconComponent =
                          sector.icon === "Zap"
                            ? Zap
                            : sector.icon === "Factory"
                              ? Factory
                              : sector.icon === "Car"
                                ? Car
                                : TreePine;
                        const colorMap: Record<string, string> = {
                          amber: "bg-amber-500",
                          blue: "bg-blue-500",
                          green: "bg-green-500",
                          emerald: "bg-emerald-500",
                        };
                        const textColorMap: Record<string, string> = {
                          amber: "text-amber-600",
                          blue: "text-blue-600",
                          green: "text-green-600",
                          emerald: "text-emerald-600",
                        };
                        const iconColorMap: Record<string, string> = {
                          amber: "text-amber-500",
                          blue: "text-blue-500",
                          green: "text-green-500",
                          emerald: "text-emerald-500",
                        };
                        return (
                          <div key={sector.sector} className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <IconComponent
                                  className={`h-4 w-4 ${iconColorMap[sector.color] ?? "text-muted-foreground"}`}
                                />
                                <span className="text-sm font-medium">
                                  {sector.sector}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {sector.percentage}%
                                </span>
                              </div>
                              <span
                                className={`text-sm font-semibold tabular-nums ${textColorMap[sector.color] ?? ""}`}
                              >
                                {sector.emissions} Mt
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className={`${colorMap[sector.color] ?? "bg-gray-500"} h-2 rounded-full transition-all duration-500`}
                                style={{ width: `${sector.percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>

                {/* Historical Trends Chart */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">
                      Historical Carbon Emissions
                    </h3>
                    <div className="text-sm text-muted-foreground">
                      {carbonData.historicalData.length} years of data
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-6">
                    <div className="h-[350px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={carbonData.historicalData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <defs>
                            <linearGradient
                              id="carbon-emissions-gradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#059669" />
                            </linearGradient>
                            <linearGradient
                              id="carbon-area-gradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#10b981"
                                stopOpacity={0.2}
                              />
                              <stop
                                offset="100%"
                                stopColor="#10b981"
                                stopOpacity={0.05}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="hsl(var(--border))"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="year"
                            stroke="hsl(var(--muted-foreground))"
                            tick={{
                              fontWeight: 500,
                              fontSize: 12,
                              fontFamily: "Inter, sans-serif",
                            }}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={10}
                          />
                          <YAxis
                            stroke="hsl(var(--muted-foreground))"
                            tick={{
                              fontWeight: 500,
                              fontSize: 12,
                              fontFamily: "Inter, sans-serif",
                            }}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={10}
                            label={{
                              value: "CO₂ Emissions (Mt)",
                              angle: -90,
                              position: "insideLeft",
                            }}
                          />
                          <RechartsTooltip
                            wrapperStyle={tooltipStyle}
                            formatter={(value) => [
                              `${value} Mt CO₂`,
                              "Emissions",
                            ]}
                            labelFormatter={(year) => `Year: ${year}`}
                          />
                          <ReferenceLine
                            y={carbonData.averageEmissions}
                            stroke="#6366f1"
                            strokeDasharray="5 3"
                            strokeWidth={1.5}
                            label={{
                              value: `Avg ${carbonData.averageEmissions} Mt`,
                              position: "insideTopRight",
                              fontSize: 11,
                              fill: "#6366f1",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="url(#carbon-emissions-gradient)"
                            strokeWidth={4}
                            dot={{
                              r: 6,
                              fill: "#10b981",
                              strokeWidth: 2,
                              stroke: "#ffffff",
                            }}
                            activeDot={{
                              r: 8,
                              stroke: "#10b981",
                              strokeWidth: 3,
                              fill: "#10b981",
                            }}
                            connectNulls={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Chart Insights */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-muted-foreground">
                          <span className="font-medium text-foreground">
                            Trend:
                          </span>{" "}
                          {carbonData.trend}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span className="text-muted-foreground">
                          <span className="font-medium text-foreground">
                            Average:
                          </span>{" "}
                          {carbonData.averageEmissions} Mt CO₂
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-muted-foreground">
                          <span className="font-medium text-foreground">
                            Peak:
                          </span>{" "}
                          {carbonData.peakEmissions} Mt ({carbonData.peakYear})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Carbon Reduction Opportunities */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-left">
                    Reduction Opportunities
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div className="rounded-xl border border-border bg-card p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 mt-1">
                          <TreePine className="h-6 w-6 text-green-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-green-600 mb-2">
                            Forest Carbon Credits
                          </h4>
                          <p className="text-muted-foreground mb-3">
                            Estimated offset:{" "}
                            {(carbonData.totalEmissions * 0.15).toFixed(1)} Mt
                            CO₂/year through reforestation programs
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-500/10 text-green-600">
                              High Impact
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              $15-30/tonne
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 mt-1">
                          <Lightbulb className="h-6 w-6 text-blue-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-blue-600 mb-2">
                            Renewable Energy
                          </h4>
                          <p className="text-muted-foreground mb-3">
                            Estimated solar/wind potential:{" "}
                            {(carbonData.totalEmissions * 0.2).toFixed(1)} Mt
                            CO₂ reduction through clean energy adoption
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-500/10 text-blue-600">
                              Medium Impact
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              ROI: 6-10 years
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/10 mt-1">
                          <Car className="h-6 w-6 text-purple-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-purple-600 mb-2">
                            Transport Electrification
                          </h4>
                          <p className="text-muted-foreground mb-3">
                            EV adoption could reduce transport emissions by
                            40-60% by 2030
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-purple-500/10 text-purple-600">
                              High Impact
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Policy support needed
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10 mt-1">
                          <Factory className="h-6 w-6 text-orange-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-orange-600 mb-2">
                            Industrial Efficiency
                          </h4>
                          <p className="text-muted-foreground mb-3">
                            Energy audits and retrofits could reduce industrial
                            emissions by 20-35%
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-orange-500/10 text-orange-600">
                              Medium Impact
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Grant funding available
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Data Source Information */}
                <DataSourceInfo
                  dataType="carbon-analytics"
                  updatedAt={carbonData.lastUpdated}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <AlertTriangle className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">
                  No Carbon Data Available
                </h3>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  Carbon emissions data is not available for this region. This
                  could be because:
                </p>
                <ul className="text-sm text-muted-foreground mt-2 list-disc list-inside">
                  <li>The region is not included in our emissions database</li>
                  <li>Data collection is still in progress</li>
                  <li>The region has minimal trackable emissions</li>
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="extreme" className="space-y-6">
            {/* ── Hero Banner ───────────────────────────────────────────────── */}
            <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-red-500/10 via-orange-600/5 to-yellow-500/10">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent" />
              <div className="relative p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex items-center gap-3 text-left">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/20">
                      <Zap className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        Extreme Weather & Hazards
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        Scientific data from NASA EONET · USGS Earthquake
                        Catalog · GDACS (UN)
                      </p>
                    </div>
                  </div>

                  {/* Summary stat pills */}
                  {extremeWeatherSummary && (
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-2 rounded-lg border border-border bg-card/60 px-4 py-2">
                        <div
                          className={cn(
                            "w-2 h-2 rounded-full",
                            extremeWeatherSummary.activeEvents > 0
                              ? "bg-green-500 animate-pulse"
                              : "bg-muted-foreground",
                          )}
                        />
                        <div>
                          <div className="text-lg font-bold">
                            {extremeWeatherSummary.activeEvents}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Active Events
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 rounded-lg border border-border bg-card/60 px-4 py-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-lg font-bold">
                            {extremeWeatherSummary.totalPast12Months}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Past 12 Months
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 rounded-lg border border-border bg-card/60 px-4 py-2">
                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-lg font-bold">
                            {extremeWeatherSummary.highestAlert ? (
                              <Badge
                                className={cn(
                                  "text-xs",
                                  ALERT_COLORS[
                                    extremeWeatherSummary.highestAlert
                                  ],
                                )}
                              >
                                {extremeWeatherSummary.highestAlert}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                None
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Highest GDACS Alert
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Loading / Error states ────────────────────────────────────── */}
            {extremeWeatherLoading && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      className="h-24 bg-muted animate-pulse rounded-xl"
                    />
                  ))}
                </div>
                <div className="h-72 bg-muted animate-pulse rounded-xl" />
                <div className="h-64 bg-muted animate-pulse rounded-xl" />
              </div>
            )}

            {!extremeWeatherLoading && extremeWeatherError && (
              <div className="flex flex-col items-center justify-center py-10 rounded-xl border border-border bg-card">
                <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                <h4 className="text-lg font-medium mb-2">
                  Unable to Load Hazard Data
                </h4>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  {extremeWeatherError}
                </p>
              </div>
            )}

            {!extremeWeatherLoading &&
              !extremeWeatherError &&
              !extremeWeatherSummary &&
              !regionCoords && (
                <div className="flex flex-col items-center justify-center py-10 rounded-xl border border-border bg-card">
                  <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
                  <h4 className="text-lg font-medium mb-2">
                    Location Not Available
                  </h4>
                  <p className="text-sm text-muted-foreground text-center">
                    Geographic coordinates for this region could not be
                    resolved. Hazard data requires a known location.
                  </p>
                </div>
              )}

            {!extremeWeatherLoading && extremeWeatherSummary && (
              <>
                {/* ── Event Type Breakdown ───────────────────────────────────── */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">Hazard Breakdown</h3>
                    <span className="text-sm text-muted-foreground">
                      5-year totals by category
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CHART_CATEGORIES.filter(
                      (cat) =>
                        (extremeWeatherSummary.eventsByCategory[cat] ?? 0) > 0,
                    ).map((cat) => {
                      const meta = CATEGORY_META[cat];
                      const IconComp = meta.icon;
                      const count =
                        extremeWeatherSummary.eventsByCategory[cat] ?? 0;
                      return (
                        <div
                          key={cat}
                          className="group relative overflow-hidden rounded-xl border border-border bg-card p-4 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                              <IconComp className={cn("h-5 w-5", meta.color)} />
                            </div>
                            <div>
                              <div className="text-xl font-bold">{count}</div>
                              <div className="text-xs text-muted-foreground">
                                {cat}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Object.keys(extremeWeatherSummary.eventsByCategory)
                    .length === 0 && (
                    <div className="text-center py-6 text-sm text-muted-foreground rounded-xl border border-border bg-card">
                      No categorised events found for this region in the past 5
                      years.
                    </div>
                  )}
                </div>

                {/* ── 5-Year Frequency Trend (grouped bar chart) ─────────────── */}
                {extremeWeatherSummary.yearlyStats.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold">
                        5-Year Frequency Trend
                      </h3>
                      <span className="text-sm text-muted-foreground">
                        Events per year by hazard type
                      </span>
                    </div>
                    <div className="rounded-xl border border-border bg-card p-6">
                      <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={extremeWeatherSummary.yearlyStats}
                            margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                            barCategoryGap="25%"
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="hsl(var(--border))"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="year"
                              stroke="hsl(var(--muted-foreground))"
                              tick={{
                                fontSize: 12,
                                fontFamily: "Inter, sans-serif",
                              }}
                              axisLine={false}
                              tickLine={false}
                              tickMargin={8}
                            />
                            <YAxis
                              allowDecimals={false}
                              stroke="hsl(var(--muted-foreground))"
                              tick={{
                                fontSize: 12,
                                fontFamily: "Inter, sans-serif",
                              }}
                              axisLine={false}
                              tickLine={false}
                              tickMargin={8}
                              label={{
                                value: "Events",
                                angle: -90,
                                position: "insideLeft",
                                offset: 10,
                                style: { fontSize: 11 },
                              }}
                            />
                            <RechartsTooltip
                              wrapperStyle={tooltipStyle}
                              formatter={(value, name) => [value, name]}
                              labelFormatter={(year) => `Year: ${year}`}
                            />
                            <Legend
                              wrapperStyle={{
                                paddingTop: "16px",
                                fontSize: "12px",
                                fontFamily: "Inter, sans-serif",
                              }}
                            />
                            {CHART_CATEGORIES.map((cat) => (
                              <Bar
                                key={cat}
                                dataKey={cat}
                                stackId="a"
                                fill={CATEGORY_META[cat].barColor}
                                name={cat}
                                radius={
                                  cat === "Other" ? [3, 3, 0, 0] : [0, 0, 0, 0]
                                }
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="mt-3 text-xs text-muted-foreground text-center">
                        Sources: NASA EONET · USGS Earthquake Catalog (M4.5+) ·
                        GDACS (UN) — stacked by hazard category
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Recent Events Table ────────────────────────────────────── */}
                {extremeWeatherSummary.recentEvents.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold">Recent Events</h3>
                      <span className="text-sm text-muted-foreground">
                        Latest {extremeWeatherSummary.recentEvents.length}{" "}
                        events
                      </span>
                    </div>
                    <div className="rounded-xl border border-border overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/50">
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">
                                Date
                              </th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">
                                Type
                              </th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                                Event
                              </th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">
                                Alert / Mag
                              </th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">
                                Source
                              </th>
                              <th className="text-left px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">
                                Status
                              </th>
                              <th className="px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {extremeWeatherSummary.recentEvents.map(
                              (event, idx) => {
                                const meta =
                                  CATEGORY_META[event.categoryLabel] ??
                                  CATEGORY_META["Other"];
                                const IconComp = meta.icon;
                                const dateStr = event.date
                                  ? new Date(event.date).toLocaleDateString(
                                      undefined,
                                      {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      },
                                    )
                                  : "—";
                                return (
                                  <tr
                                    key={`${event.id}-${idx}`}
                                    className="hover:bg-muted/30 transition-colors"
                                  >
                                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground text-xs">
                                      {dateStr}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center gap-1.5">
                                        <IconComp
                                          className={cn(
                                            "h-4 w-4 flex-shrink-0",
                                            meta.color,
                                          )}
                                        />
                                        <span className="text-xs font-medium">
                                          {event.categoryLabel}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 max-w-[220px]">
                                      <span className="line-clamp-2 leading-snug">
                                        {event.title}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      {event.alertLevel ? (
                                        <Badge
                                          className={cn(
                                            "text-xs border",
                                            ALERT_COLORS[event.alertLevel],
                                          )}
                                        >
                                          {event.alertLevel}
                                        </Badge>
                                      ) : event.magnitude != null ? (
                                        <span className="text-xs font-medium tabular-nums">
                                          {event.magnitudeUnit}{" "}
                                          {event.magnitude.toFixed(1)}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <Badge
                                        className={cn(
                                          "text-xs",
                                          SOURCE_COLORS[event.source],
                                        )}
                                      >
                                        {event.source}
                                      </Badge>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span
                                        className={cn(
                                          "inline-flex items-center text-xs font-medium",
                                          event.status === "open"
                                            ? "text-green-600"
                                            : "text-muted-foreground",
                                        )}
                                      >
                                        {event.status === "open" ? (
                                          <span className="flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                            Active
                                          </span>
                                        ) : (
                                          "Closed"
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <a
                                        href={event.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-muted-foreground hover:text-primary transition-colors"
                                        title="View on source platform"
                                      >
                                        <ExternalLink className="h-4 w-4" />
                                      </a>
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {extremeWeatherSummary.recentEvents.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-border bg-card">
                    <Zap className="h-12 w-12 text-muted-foreground mb-4" />
                    <h4 className="text-lg font-medium mb-2">
                      No Recorded Events
                    </h4>
                    <p className="text-sm text-muted-foreground text-center max-w-sm">
                      No significant hazard events were recorded for this region
                      in the past 5 years across NASA EONET, USGS, and GDACS
                      datasets.
                    </p>
                  </div>
                )}

                {/* ── Data Source Attribution ────────────────────────────────── */}
                <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-3">
                  <h4 className="text-sm font-semibold">Data Sources</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500/15 text-blue-600 text-xs">
                          EONET
                        </Badge>
                        <span className="font-medium text-foreground">
                          NASA EONET v3
                        </span>
                      </div>
                      <p>
                        Earth Observatory Natural Event Tracker. Covers
                        wildfires, storms, floods, volcanoes, and more. Open
                        &amp; free.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-orange-500/15 text-orange-600 text-xs">
                          USGS
                        </Badge>
                        <span className="font-medium text-foreground">
                          USGS Earthquake Catalog
                        </span>
                      </div>
                      <p>
                        Authoritative seismic data from the US Geological
                        Survey. Filtered to M4.5+ events within the region.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-500/15 text-red-600 text-xs">
                          GDACS
                        </Badge>
                        <span className="font-medium text-foreground">
                          GDACS (UN / EC)
                        </span>
                      </div>
                      <p>
                        Global Disaster Alerting Coordination System. UN-backed
                        multi-hazard alert platform with Red / Orange / Yellow
                        alert levels.
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground pt-1">
                    Data generated:{" "}
                    {extremeWeatherSummary.generatedAt
                      ? new Date(
                          extremeWeatherSummary.generatedAt,
                        ).toLocaleString()
                      : "—"}
                  </p>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
};

export default EnrichedDataTab;
